import { useEffect, useState } from "react";
import shero1 from "../../assets/hero1.jpg";
import shero2 from "../../assets/hero2.jpg";
import shero3 from "../../assets/hero3.jpg";
import inventoryGif from "../../assets/inventory-management.gif";
import Footer from "../Footer";
import Navbar from "../Navbar";
import "./android-app.css";
import { Helmet } from "react-helmet-async";

export default function App() {
  const heroImages = [shero1, shero2, shero3];
  const totalSlides = heroImages.length;
  const [currentSlide, setCurrentSlide] = useState(0);

  /* ================= HERO SLIDESHOW (FIXED) ================= */
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 5000);

    return () => clearInterval(interval);
  }, [totalSlides]);

  /* ================= TIMELINE PROGRESS ================= */
  useEffect(() => {
    const timeline = document.getElementById("brand-timeline");
    if (!timeline) return;

    const line = timeline.querySelector(".timeline-line");
    const items = timeline.querySelectorAll(".timeline-item");

    const onScroll = () => {
      const rect = timeline.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const progress = Math.min(
        Math.max(
          (windowHeight - rect.top) / (rect.height + windowHeight),
          0
        ),
        1
      );

      const lineHeight = progress * rect.height;
      line.style.height = `${lineHeight}px`;

      items.forEach((item) => {
        const itemOffset = item.offsetTop;
        if (lineHeight >= itemOffset + 20) {
          item.classList.add("active");
        } else {
          item.classList.remove("active");
        }
      });
    };

    window.addEventListener("scroll", onScroll);
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ================= FADE-UP ANIMATION ================= */
  useEffect(() => {
    const elements = document.querySelectorAll(".animate");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.2 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
    <Helmet>
  <title>
    AI Chatbot Services | Conversational AI & Chatbot Development | Manovate Technologies
  </title>

  <meta
    name="description"
    content="Manovate Technologies provides AI chatbot services to automate customer support, sales, and enterprise workflows. We build intelligent conversational AI chatbots using NLP and machine learning for websites, mobile apps, and business systems."
  />

  <link
    rel="canonical"
    href="https://www.manovate.co.in/itservices/ai-chatbot-services"
  />
</Helmet>

    <Navbar />
      {/* ================= HERO ================= */}
      <section className="hero-modern">
        <div className="hero-slideshow">
          {heroImages.map((img, index) => (
            <div
              key={index}
              className={`hero-slide ${
                index === currentSlide ? "active" : ""
              }`}
              style={{ backgroundImage: `url(${img})` }}
            />
          ))}
        </div>

        <div className="hero-overlay"></div>

        <div className="service-hero-content animate">
          <h1>AI Chatbot Services</h1>
          <p>
            Manovate Technologies delivers AI-powered chatbot services that help
            businesses automate conversations, enhance customer engagement, and
            provide instant support across digital channels.
          </p>
          <a href="#services" className="btn-primary">
            Build a Chatbot
          </a>
        </div>
      </section>

      {/* ================= OVERVIEW ================= */}
      <section className="section-white">
        <div className="service-container">
          <h2>Overview</h2>
          <p className="section-intro">
            Our AI chatbot services help businesses automate customer
            interactions using conversational AI. We build intelligent chatbots
            for websites, mobile apps, CRM platforms, and enterprise systems.
          </p>

          <div className="grid">
            <div className="card animate">
              <h3>Customer Support Chatbots</h3>
              <p>
                24/7 automated customer support, FAQs, ticket handling, and
                real-time assistance using AI chatbots.
              </p>
            </div>

            <div className="card animate">
              <h3>Sales & Lead Generation Chatbots</h3>
              <p>
                Engage visitors, qualify leads, schedule meetings, and drive
                conversions using conversational AI.
              </p>
            </div>

            <div className="card animate">
              <h3>Enterprise & Internal Chatbots</h3>
              <p>
                Internal chatbots for HR, IT support, onboarding, and knowledge
                management to improve productivity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= TIMELINE ================= */}
      <section className="section-light">
        <div className="service-container">
          <h2>Our Chatbot Development Process</h2>
          <p className="section-intro">
            A structured approach to deliver scalable, accurate, and
            business-ready AI chatbot solutions.
          </p>

          <div className="timeline" id="brand-timeline">
            <div className="timeline-start"></div>
            <div className="timeline-line"></div>

            <div className="timeline-item">
              <span className="timeline-label">Discovery</span>
              <h3>Use Case & Conversation Design</h3>
              <p>
                Identify chatbot use cases, user intents, and conversation flows
                to design effective interactions.
              </p>
            </div>

            <div className="timeline-item">
              <span className="timeline-label">Development</span>
              <h3>AI Chatbot Development & Training</h3>
              <p>
                Build and train chatbots using NLP and machine learning models
                for accurate responses.
              </p>
            </div>

            <div className="timeline-item">
              <span className="timeline-label">Deployment</span>
              <h3>Integration & Optimization</h3>
              <p>
                Deploy chatbots across platforms and continuously optimize
                performance using real user data.
              </p>
            </div>

            <div className="timeline-end"></div>
          </div>
        </div>
      </section>

      {/* ================= CONTACT ================= */}
      <section className="contact-section" id="contact">
        <div className="service-container contact-grid">
          <div className="contact-left">
            <h2>Start Your Chatbot Project</h2>
            <p>
              Ready to automate conversations and improve customer engagement?
              Talk to our chatbot experts today.
            </p>

            <form
              className="service-contact-form"
              onSubmit={(e) => {
                e.preventDefault();
                alert("Thank you! Our chatbot team will contact you shortly.");
              }}
            >
              <input type="text" placeholder="Your name*" required />
              <input type="email" placeholder="Your email*" required />
              <textarea
                rows="4"
                placeholder="Tell us about your chatbot requirements..."
                required
              ></textarea>
              <button type="submit" className="submit-btn">
                Submit Project
              </button>
            </form>
          </div>

          <div className="contact-right gif-container">
            <img
              src={inventoryGif}
              alt="AI Chatbot Services"
              className="contact-gif"
            />
          </div>
        </div>
      </section>
      <p className="service-backlink">
  Explore our complete range of{" "}
  <a href="/itservices">IT Services & Software Engineering Solutions</a>.
</p>
<br />
<br />
      <Footer />
    </>
  );
}
